// The world's view tier (gate C1): @rpg/stream's `Streamer` loads each of @rpg/world's chunks as a few small build
// steps under its per-frame budget, ahead of the hero (its predicted and view anchors), warms it before it shows and
// disposes it when it goes. A chunk draws as one mesh on the low-poly look's surface material (its terrain facets, its
// scatter and its props merged), its kit placements as one batch on the kit surface, and the water over its square
// (places-view.ts, phase C joined). Chunk colours are albedo baked from the palette: the time of day changes the light,
// the haze and the sky, never the palette, so nothing is rebaked when the hour turns. Also the top-down overview of the
// whole layout, for the evidence shot. View only: it reads the world and the sim, never writes them. Every part of a
// chunk's mesh carries the foliage push's `sway` weight (the scatter's from its look, decision 0085; zeros elsewhere).
import * as THREE from "three";
import { ensureSway, type LowPolyLook, type LowPolySceneLook } from "@rpg/look";
import { merge } from "@rpg/meshkit";
import type { Runtime } from "@rpg/runtime";
import { Streamer, type ChunkSpec } from "@rpg/stream";
import { createPieceCache, fbm, placeItems, subSeed, terrainGeometry, type PieceCache, type PieceLook, type ScatterItem, type World } from "@rpg/world";
import type { WorldContent } from "./content";
import type { WorldPlacesView } from "./places-view";

/** The view tier. */
export interface WorldView {
  streamer: Streamer;
  pieces: PieceCache;
  /** Build what the hero needs now (blocking: the boot's prime; a warp is a cut the streamer finds itself). */
  prime(): void;
  stats(): Record<string, unknown>;
  /** The drawn frames' counts since the last reset: draw calls and triangles, max and mean (counts, not times). */
  drawn(): { frames: number; maxCalls: number; maxTriangles: number; meanCalls: number; meanTriangles: number };
  resetDrawn(): void;
  /** Draw the whole layout from above every frame (the overview shot) until called with false. */
  overview(on?: boolean): { chunks: number; triangles: number };
}

export function mountWorldView(o: {
  world: World;
  content: WorldContent;
  look: LowPolyLook;
  /** The scene look in use (the overview draws it without haze, then puts it back unless `restoreLook` is given). */
  sceneLook: LowPolySceneLook;
  /** Put the current look back after the overview (the sky's time of day); default `look.setLook(sceneLook)`. */
  restoreLook?: () => void;
  /** The kit, the props and the water per chunk. */
  places: WorldPlacesView;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  runtime: Runtime;
  tier: string;
  /** The hero's feet and ground velocity. */
  hero(): { x: number; z: number; vx: number; vz: number };
}): WorldView {
  const { world, content, look } = o;
  // the surface material is shared by every chunk and figure: the streamer must never dispose it
  look.surface.userData.resident = true;
  const lookOf: Record<string, PieceLook> = {};
  for (const [piece, l] of Object.entries(content.looks)) lookOf[piece] = { kind: l.kind, colours: l.colours.map((c) => look.palette(c)), size: l.size ?? 1, sway: l.sway ?? 0 };
  const pieces = createPieceCache(lookOf, 5, 1);
  pieces.warm();
  const surfaceColours = new Map<string, THREE.Color[]>();
  const coloursFor = (key: string): THREE.Color[] | undefined => {
    let c = surfaceColours.get(key);
    if (!c && content.surfaces[key]) surfaceColours.set(key, (c = content.surfaces[key]!.map((k) => look.palette(k))));
    return c;
  };
  const fallback = [new THREE.Color("#7cb85a")];
  const patchSeed = subSeed(world.seed, "ground-colour");
  /** A ground colour at (x, z): the surface's colours (a region's own first), picked by a hash of the cell. */
  const colourAt = (x: number, z: number): THREE.Color => {
    // the ground's own tag (the terrain's): a floor's or a deck's never colours the ground under it
    const tag = world.terrain.surfaceAt(x, z);
    const region = world.terrain.regionAt(x, z);
    const list = (region ? coloursFor(`${region}.${tag}`) : undefined) ?? coloursFor(tag) ?? fallback;
    // patches of each colour a few metres across (smooth noise), not a checkerboard
    const n = fbm(patchSeed, x, z, 7, 2) * 0.5 + 0.5;
    return list[Math.min(list.length - 1, Math.floor(n * list.length))]!;
  };
  const big = new Set(["tree", "pine", "boulder", "bush"]);

  const specs: ChunkSpec[] = world.chunks.map((c) => ({
    id: c.id,
    area: c.area,
    steps: (b) => {
      let terrain: THREE.BufferGeometry | null = null;
      let items: readonly ScatterItem[] = [];
      let bigGeo: THREE.BufferGeometry | null = null;
      let smallGeo: THREE.BufferGeometry | null = null;
      return [
        { name: "terrain", run: () => void (terrain = terrainGeometry(world.heights(c.id), colourAt, { seed: c.i * 977 + c.j })) },
        { name: "scatter", run: () => void (items = world.scatter(c.id)) },
        { name: "place:big", run: () => void (bigGeo = placeItems(items.filter((i) => big.has(i.rule)), (i) => pieces.get(i.piece, i.seed))) },
        { name: "place:small", run: () => void (smallGeo = placeItems(items.filter((i) => !big.has(i.rule)), (i) => pieces.get(i.piece, i.seed))) },
        {
          name: "mesh",
          run: () => {
            const props = o.places.props(c.id);
            const geo = merge([terrain!, bigGeo!, smallGeo!, ...(props ? [props] : [])].filter((g) => g.getAttribute("position").count > 0).map(ensureSway));
            terrain!.dispose();
            bigGeo!.dispose();
            smallGeo!.dispose();
            props?.dispose();
            geo.computeBoundingSphere();
            const mesh = new THREE.Mesh(geo, look.surface);
            mesh.name = `chunk-mesh:${c.id}`;
            // a caster where the tier says every mesh casts (the look's track decides; trees shade the ground)
            mesh.castShadow = true;
            b.group.add(mesh);
          },
        },
        {
          name: "places",
          run: () => {
            const kit = o.places.kitMesh(c.id);
            if (kit) b.group.add(kit);
            const water = o.places.waterMesh(c.area);
            if (water) b.group.add(water);
            look.track(b.group);
          },
        },
      ];
    },
  }));

  const s = content.streaming;
  const streamer = new Streamer(o.renderer, o.scene, o.camera, specs, { ...s.view, budgetMs: s.budgetMs, lateBudgetMs: s.lateBudgetMs, predictS: s.predictS, teleportM: s.teleportM });
  const viewAnchor = (): { x: number; z: number } => {
    const d = new THREE.Vector3();
    o.camera.getWorldDirection(d);
    const l = Math.hypot(d.x, d.z) || 1;
    return { x: o.camera.position.x + (d.x / l) * 40, z: o.camera.position.z + (d.z / l) * 40 };
  };
  let primed = false;
  // the overview mode (the evidence shot): the whole layout drawn from above every frame until it is turned off
  let ov: { group: THREE.Group; saved: { far: number; near: number; fov: number }; sky: boolean; bg: THREE.Scene["background"] } | null = null;
  const b = world.layout.bounds;
  const centre = new THREE.Vector3((b.x0 + b.x1) / 2, 0, (b.z0 + b.z1) / 2);
  o.runtime.on(
    "late",
    (c) => {
      if (ov) {
        // after the camera rig and the hero's pose: park the camera over the valley, hide the streamed chunks
        const cam = o.camera;
        const span = Math.max(b.x1 - b.x0, b.z1 - b.z0);
        const height = (span / 2 / Math.tan(((cam.fov / 2) * Math.PI) / 180)) * 1.02 * Math.max(1, 1 / cam.aspect);
        cam.position.set(centre.x, height, centre.z + 0.01);
        cam.up.set(0, 0, -1);
        cam.lookAt(centre);
        for (const ch of o.scene.children) if (ch.name.startsWith("chunk:")) ch.visible = false;
        look.focus(centre);
        // no haze over the whole layout (after the sky's frame has written this frame's)
        look.uniforms.uLpHaze2.value.y = 0;
        return;
      }
      if (!primed || (c.mode === "play" && o.runtime.paused)) return;
      const h = o.hero();
      streamer.update(h.x, h.z, h.vx, h.vz, viewAnchor());
    },
    { order: 1e6, name: "world view: stream" },
  );

  // what each drawn frame cost (renderer.info counts the whole frame: the look resets it before drawing): counts
  // for the route's report, not frame times
  const drawn = { frames: 0, maxCalls: 0, maxTriangles: 0, sumCalls: 0, sumTriangles: 0 };
  o.runtime.on(
    "render",
    () => {
      const r = o.renderer.info.render;
      drawn.frames++;
      drawn.maxCalls = Math.max(drawn.maxCalls, r.calls);
      drawn.maxTriangles = Math.max(drawn.maxTriangles, r.triangles);
      drawn.sumCalls += r.calls;
      drawn.sumTriangles += r.triangles;
    },
    { order: 1e6, name: "world view: frame counts" },
  );

  return {
    streamer,
    pieces,
    drawn: () => ({ frames: drawn.frames, maxCalls: drawn.maxCalls, maxTriangles: drawn.maxTriangles, meanCalls: drawn.frames ? drawn.sumCalls / drawn.frames : 0, meanTriangles: drawn.frames ? drawn.sumTriangles / drawn.frames : 0 }),
    resetDrawn: () => void Object.assign(drawn, { frames: 0, maxCalls: 0, maxTriangles: 0, sumCalls: 0, sumTriangles: 0 }),
    prime() {
      const h = o.hero();
      streamer.prime(h.x, h.z, { view: viewAnchor() });
      primed = true;
    },
    stats: () => ({ ...streamer.stats(), pieces: pieces.report() }),
    overview(on = true) {
      if (!on) {
        if (!ov) return { chunks: 0, triangles: 0 };
        // put the view back: the streamed chunks, the camera's lens, the sky, the haze
        o.scene.remove(ov.group);
        ov.group.traverse((x) => {
          const m = x as THREE.Mesh;
          if (m.isMesh) m.geometry.dispose();
        });
        for (const ch of o.scene.children) if (ch.name.startsWith("chunk:")) ch.visible = true;
        Object.assign(o.camera, ov.saved);
        o.camera.up.set(0, 1, 0);
        o.camera.updateProjectionMatrix();
        look.sky.visible = ov.sky;
        o.scene.background = ov.bg;
        if (o.restoreLook) o.restoreLook();
        else look.setLook(o.sceneLook);
        ov = null;
        return { chunks: 0, triangles: 0 };
      }
      if (ov) o.scene.remove(ov.group);
      // the whole layout at once: terrain and the big scatter of every chunk, the placements, the water
      const group = new THREE.Group();
      group.name = "world-overview";
      let triangles = 0;
      for (const c of world.chunks) {
        const t = terrainGeometry(world.heights(c.id), colourAt, { seed: c.i * 977 + c.j });
        const items = world.scatter(c.id).filter((i) => big.has(i.rule));
        const props = o.places.props(c.id);
        const g = items.length || props ? merge([t, ...(items.length ? [placeItems(items, (i) => pieces.get(i.piece, i.seed))] : []), ...(props ? [props] : [])].map(ensureSway)) : t;
        triangles += g.getAttribute("position").count / 3;
        group.add(new THREE.Mesh(g, look.surface));
        const kit = o.places.kitMesh(c.id);
        if (kit) group.add(kit);
        const water = o.places.waterMesh(c.area);
        if (water) group.add(water);
      }
      o.scene.add(group);
      const cam = o.camera;
      ov = { group, saved: ov?.saved ?? { far: cam.far, near: cam.near, fov: cam.fov }, sky: ov?.sky ?? look.sky.visible, bg: ov ? ov.bg : o.scene.background };
      Object.assign(cam, { fov: 40, near: 10, far: 2000 });
      cam.updateProjectionMatrix();
      look.sky.visible = false;
      o.scene.background = new THREE.Color("#2c3a2a");
      look.setLook({ ...o.sceneLook, haze: { ...o.sceneLook.haze, max: 0 } });
      return { chunks: world.chunks.length, triangles };
    },
  };
}
